import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, List, LayoutGrid, Receipt, AlertTriangle } from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatDate, formatCurrency, STAGE_STATUS_CONFIG } from '../lib/utils';
import { StageBadge, InvoiceBadge } from '../components/Badge';
import ProgressBar from '../components/ProgressBar';
import { parseISO, differenceInDays, format, startOfMonth, addMonths, endOfMonth } from 'date-fns';

type View = 'gantt' | 'list' | 'kanban';

// ────────────────────────────────────────────────
// Gantt Chart (SVG-based)
// ────────────────────────────────────────────────
function GanttChart({ projectId }: { projectId: string }) {
  const stages = useStore((s) => s.stages)
    .filter((s) => s.projectId === projectId)
    .sort((a, b) => a.order - b.order);
  const invoices = useStore((s) => s.invoices).filter((i) => i.projectId === projectId);
  const project = useStore((s) => s.projects).find((p) => p.id === projectId);

  if (stages.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400">
        <Calendar size={32} className="mx-auto mb-3 text-gray-200" />
        <p>No stages defined yet. Add stages in the project detail page.</p>
      </div>
    );
  }

  const allDates = stages.flatMap((s) => [parseISO(s.plannedStart), parseISO(s.plannedEnd)]);
  const minDate = new Date(Math.min(...allDates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())));

  const chartStart = startOfMonth(minDate);
  const chartEnd = endOfMonth(addMonths(maxDate, 0));
  const totalDays = differenceInDays(chartEnd, chartStart) + 1;

  const ROW_H = 48;
  const LABEL_W = 180;
  const DAY_W = Math.max(8, Math.min(24, 1200 / totalDays));
  const CHART_W = totalDays * DAY_W;
  const HEADER_H = 40;

  // Month headers
  const months: { label: string; x: number; width: number }[] = [];
  let cur = new Date(chartStart);
  while (cur <= chartEnd) {
    const monthEnd = endOfMonth(cur);
    const clampedEnd = monthEnd < chartEnd ? monthEnd : chartEnd;
    const startOff = differenceInDays(cur, chartStart);
    const daysInRange = differenceInDays(clampedEnd, cur) + 1;
    months.push({
      label: format(cur, 'MMM yyyy'),
      x: startOff * DAY_W,
      width: daysInRange * DAY_W,
    });
    cur = addMonths(startOfMonth(cur), 1);
  }

  const today = new Date();
  const todayX = differenceInDays(today, chartStart) * DAY_W;

  const COLORS: Record<string, { bar: string; text: string }> = {
    not_started: { bar: '#e5e7eb', text: '#6b7280' },
    in_progress: { bar: '#3b82f6', text: '#1d4ed8' },
    blocked: { bar: '#ef4444', text: '#b91c1c' },
    completed: { bar: '#22c55e', text: '#15803d' },
  };

  const totalH = HEADER_H + stages.length * ROW_H + 20;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <div className="flex" style={{ minWidth: LABEL_W + CHART_W }}>
          {/* Left labels */}
          <div className="shrink-0" style={{ width: LABEL_W }}>
            <div className="h-10 border-b border-gray-100 flex items-center px-4 bg-gray-50">
              <span className="text-xs font-medium text-gray-400">Stage</span>
            </div>
            {stages.map((stage, i) => {
              const color = COLORS[stage.status];
              return (
                <div
                  key={stage.id}
                  className="flex items-center gap-2 px-4 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                  style={{ height: ROW_H }}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: color.bar }}
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate leading-tight">{stage.title}</p>
                    <p className="text-xs text-gray-400 leading-tight">{stage.completionPercent}%</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* SVG chart */}
          <div className="flex-1 overflow-hidden">
            <svg width={CHART_W} height={totalH} className="block">
              {/* Month headers */}
              {months.map((m) => (
                <g key={m.label}>
                  <rect x={m.x} y={0} width={m.width} height={HEADER_H} fill={months.indexOf(m) % 2 === 0 ? '#f9fafb' : '#f3f4f6'} />
                  <text x={m.x + m.width / 2} y={HEADER_H / 2 + 5} textAnchor="middle" fontSize={11} fill="#9ca3af" fontWeight={500}>
                    {m.label}
                  </text>
                  <line x1={m.x} y1={0} x2={m.x} y2={totalH} stroke="#e5e7eb" strokeWidth={1} />
                </g>
              ))}

              {/* Stage bars */}
              {stages.map((stage, i) => {
                const y = HEADER_H + i * ROW_H;
                const barY = y + 12;
                const barH = ROW_H - 24;
                const startX = differenceInDays(parseISO(stage.plannedStart), chartStart) * DAY_W;
                const endX = (differenceInDays(parseISO(stage.plannedEnd), chartStart) + 1) * DAY_W;
                const barW = Math.max(endX - startX, 4);
                const color = COLORS[stage.status];
                const progressW = (barW * stage.completionPercent) / 100;
                const linked = invoices.find((inv) => inv.relatedStageId === stage.id);

                return (
                  <g key={stage.id}>
                    {/* Row bg */}
                    <rect x={0} y={y} width={CHART_W} height={ROW_H} fill={i % 2 === 0 ? 'white' : '#fafafa'} />
                    {/* Row divider */}
                    <line x1={0} y1={y + ROW_H} x2={CHART_W} y2={y + ROW_H} stroke="#f3f4f6" strokeWidth={1} />
                    {/* Bar background */}
                    <rect x={startX} y={barY} width={barW} height={barH} rx={4} fill={color.bar} opacity={0.3} />
                    {/* Bar progress */}
                    {stage.completionPercent > 0 && (
                      <rect x={startX} y={barY} width={progressW} height={barH} rx={4} fill={color.bar} opacity={0.9} />
                    )}
                    {/* Invoice diamond */}
                    {linked && (
                      <g transform={`translate(${endX - 6}, ${y + ROW_H / 2})`}>
                        <polygon
                          points="0,-6 6,0 0,6 -6,0"
                          fill={linked.status === 'paid' ? '#22c55e' : linked.status === 'overdue' ? '#ef4444' : '#f59e0b'}
                          opacity={0.9}
                        />
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Today line */}
              {todayX >= 0 && todayX <= CHART_W && (
                <g>
                  <line x1={todayX} y1={HEADER_H} x2={todayX} y2={totalH} stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />
                  <text x={todayX + 3} y={HEADER_H + 12} fontSize={9} fill="#6366f1" fontWeight={600}>Today</text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="px-5 py-3 border-t border-gray-50 flex items-center gap-5 flex-wrap">
        {Object.entries(STAGE_STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
            <div className={`w-3 h-3 rounded-sm ${cfg.bg}`} />
            {cfg.label}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-4">
          <span>◆</span> Invoice milestone
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <div className="w-4 border-t-2 border-dashed border-indigo-400" />
          Today
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// List View
// ────────────────────────────────────────────────
function ListView({ projectId }: { projectId: string }) {
  const stages = useStore((s) => s.stages)
    .filter((s) => s.projectId === projectId)
    .sort((a, b) => a.order - b.order);
  const invoices = useStore((s) => s.invoices).filter((i) => i.projectId === projectId);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-50 bg-gray-50">
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">#</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Stage</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Timeline</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Progress</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Invoice</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {stages.map((stage) => {
            const linked = invoices.find((i) => i.relatedStageId === stage.id);
            return (
              <tr key={stage.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-4 text-sm font-medium text-gray-400">{stage.order}</td>
                <td className="px-5 py-4">
                  <div className="font-medium text-sm text-gray-900">{stage.title}</div>
                  {stage.description && <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{stage.description}</div>}
                  {stage.status === 'blocked' && stage.blockerNote && (
                    <div className="flex items-center gap-1 text-xs text-red-500 mt-0.5">
                      <AlertTriangle size={10} /> {stage.blockerNote}
                    </div>
                  )}
                </td>
                <td className="px-5 py-4"><StageBadge status={stage.status} /></td>
                <td className="px-5 py-4 text-xs text-gray-500">
                  {formatDate(stage.plannedStart)}<br />→ {formatDate(stage.plannedEnd)}
                </td>
                <td className="px-5 py-4 w-32">
                  <ProgressBar value={stage.completionPercent} showLabel />
                </td>
                <td className="px-5 py-4">
                  {linked ? (
                    <div>
                      <InvoiceBadge status={linked.status} />
                      <div className="text-xs text-gray-400 mt-0.5">{formatCurrency(linked.amount)}</div>
                    </div>
                  ) : <span className="text-gray-300 text-xs">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────
// Kanban View
// ────────────────────────────────────────────────
function KanbanView({ projectId }: { projectId: string }) {
  const stages = useStore((s) => s.stages).filter((s) => s.projectId === projectId);
  const invoices = useStore((s) => s.invoices).filter((i) => i.projectId === projectId);

  const COLUMNS: { status: string; label: string; color: string }[] = [
    { status: 'not_started', label: 'Not Started', color: 'border-t-gray-300' },
    { status: 'in_progress', label: 'In Progress', color: 'border-t-blue-400' },
    { status: 'blocked', label: 'Blocked', color: 'border-t-red-400' },
    { status: 'completed', label: 'Completed', color: 'border-t-green-400' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {COLUMNS.map((col) => {
        const colStages = stages.filter((s) => s.status === col.status).sort((a, b) => a.order - b.order);
        return (
          <div key={col.status}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">{col.label}</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{colStages.length}</span>
            </div>
            <div className="space-y-3">
              {colStages.map((stage) => {
                const linked = invoices.find((i) => i.relatedStageId === stage.id);
                return (
                  <div key={stage.id} className={`bg-white rounded-xl border-t-2 border border-gray-100 p-4 ${col.color}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-800 leading-snug">{stage.title}</span>
                      <span className="text-xs text-gray-400 shrink-0">#{stage.order}</span>
                    </div>
                    <div className="text-xs text-gray-400 mb-2.5">
                      {formatDate(stage.plannedStart)} → {formatDate(stage.plannedEnd)}
                    </div>
                    <ProgressBar value={stage.completionPercent} showLabel />
                    {linked && (
                      <div className="mt-2.5 pt-2.5 border-t border-gray-50">
                        <InvoiceBadge status={linked.status} />
                      </div>
                    )}
                    {stage.status === 'blocked' && stage.blockerNote && (
                      <div className="mt-2 text-xs text-red-500 flex items-start gap-1">
                        <AlertTriangle size={10} className="mt-0.5 shrink-0" /> {stage.blockerNote}
                      </div>
                    )}
                  </div>
                );
              })}
              {colStages.length === 0 && (
                <div className="border-2 border-dashed border-gray-100 rounded-xl p-4 text-center text-xs text-gray-300">
                  No stages
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────
// Timeline page
// ────────────────────────────────────────────────
export default function Timeline() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const [view, setView] = useState<View>('gantt');

  const project = projects.find((p) => p.id === projectId);
  const client = clients.find((c) => c.id === project?.clientId);

  if (!project) {
    return <div className="p-8 text-center text-gray-400">Project not found.</div>;
  }

  const VIEWS = [
    { id: 'gantt' as View, label: 'Gantt', icon: Calendar },
    { id: 'list' as View, label: 'List', icon: List },
    { id: 'kanban' as View, label: 'Kanban', icon: LayoutGrid },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={() => navigate(`/projects/${projectId}`)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft size={14} /> {project.name}
      </button>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Timeline</h1>
          <p className="text-gray-400 text-sm mt-0.5">{client?.companyName} · {project.name}</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {VIEWS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setView(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === id ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'gantt' && <GanttChart projectId={project.id} />}
      {view === 'list' && <ListView projectId={project.id} />}
      {view === 'kanban' && <KanbanView projectId={project.id} />}
    </div>
  );
}
